import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Grid, Text, Title } from '@tremor/react';
import { CircleCheck, CircleDot, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react';
import { extractErrorMessage, getMaxHealth } from '@/lib/api';

// [MAX_HEALTH 27/10] Saúde diária do canário JusQuant MAX v1 (router
// admin-api/routers/max_health.py). Reconstruído em 29/10 a partir do bundle
// index-DDB4VyWI.js: o fonte original (Codex, 27/10) nunca foi commitado e o
// deploy da aba Financeiro em 28/10 apagou a aba do menu.

export interface Ci95 { low_pct: number; high_pct: number }
export interface MaxRollout {
  target_pct: number;
  release_id: string;
  eligible_new_users: number;
  assigned_users: number;
  actual_assignment_pct: number | null;
  assignment_ci95: Ci95 | null;
  users_who_selected_max: number;
  selection_rate_among_assigned_pct: number | null;
  sample_status: string;
}
interface Taxa { value_pct: number | null; sample_n: number; status: string }
interface Confiabilidade {
  total: number;
  completed: number;
  completed_missing_artifact: number;
  failed_or_cancelled: number;
  in_progress: number;
  runner_db_mismatch: number;
  completion_rate: Taxa;
  failure_rate: Taxa;
  latency_minutes_p50: number | null;
  latency_minutes_p95: number | null;
}
export interface Retencao {
  rate_pct: number | null;
  retained: number;
  mature_activated_users: number;
  ci95: Ci95 | null;
  sample_status: string;
}
export interface BracoAb {
  users: number;
  active_users: number;
  cases_run: number;
  standard_cases: number;
  max_cases: number;
  max_case_share_pct: number | null;
  cases_per_active_user: number | null;
  completed: number;
  completion_rate_pct: number | null;
  errors: number;
  error_rate_pct: number | null;
  blocking_errors: number;
  blocking_error_rate_pct: number | null;
  retention_7d: Retencao;
  retention_28d: Retencao;
  customer_spend_brl: number;
  purchases: number;
  revenue_per_user_brl: number | null;
  token_cost_usd: number;
  token_cost_brl: number;
  input_tokens: number;
  output_tokens: number;
  cost_coverage_pct: number | null;
  cost_cases: number;
  cost_per_case_usd: number | null;
  observed_margin_brl: number;
  observed_margin_pct: number | null;
  margin_status: string;
  status_counts: Record<string, number>;
}
export interface AbAnalysis {
  control: BracoAb;
  test: BracoAb;
  control_label: string;
  test_label: string;
  sample_status: string;
  usd_brl_rate: number;
  definitions: Record<string, string>;
  revenue_attribution: {
    direct_purchases: number;
    purchases_in_experiment_orgs: number;
    coverage_pct: number | null;
    shared_orgs_across_arms: number;
    note: string;
  };
}
export interface MaxHealthResponse {
  disponivel: boolean;
  erro?: string;
  window: { cutoff: string };
  rollout: MaxRollout;
  health: { state: 'red' | 'green' | string; blockers: string[]; warnings: string[] };
  reliability: { max: Confiabilidade; control_standard: Confiabilidade };
  quality: {
    human_review: {
      coverage_pct: number | null;
      reviewed_live_cases: number;
      completed_cases: number;
      note: string;
      pending_case_ids: number[];
    };
    release_gates?: Record<string, { passed?: number; failed?: number }>;
  };
  economics: {
    net_credits: number;
    credits_consumed: number;
    credits_refunded: number;
    credits_per_max_case_configured: number;
    cost_usd_p50: number | null;
    cost_usd_p95: number | null;
    cost_coverage_pct: number | null;
  };
  cases: {
    id: number;
    status: string;
    pdf: boolean;
    pjc: boolean;
    latency_minutes: number | null;
    ai_cost_usd: number | null;
    retries: number;
  }[];
  data_gaps: string[];
  ab_analysis?: AbAnalysis;
}

const JANELAS = [7, 30, 90];

function pct(v: number | null | undefined) {
  return v == null ? '—' : `${v.toFixed(1)}%`;
}
function usd(v: number | null | undefined) {
  return v == null ? '—' : `US$ ${v.toFixed(3)}`;
}
function minutos(v: number | null | undefined) {
  if (v == null) return '—';
  return v < 60 ? `${v.toFixed(0)} min` : `${(v / 60).toFixed(1)} h`;
}

function Amostra({ n, status }: { n: number; status: string }) {
  if (status === 'insufficient_sample')
    return (
      <span className="rounded-full border border-amber-700/50 bg-amber-900/20 px-2 py-0.5 text-xs text-amber-300">
        n={n} · amostra insuficiente
      </span>
    );
  return <span className="text-xs text-slate-400">n={n}</span>;
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card>
      <Text>{label}</Text>
      <div className="mt-2 text-2xl font-semibold text-slate-100">{value}</div>
      <div className="mt-1 text-xs text-slate-400">{hint}</div>
    </Card>
  );
}

function corStatus(s: string) {
  if (['COMPLETED', 'DONE', 'VALIDATED'].includes(s)) return 'text-emerald-300';
  if (['FAILED', 'ERROR', 'CANCELLED', 'BLOCKED'].includes(s)) return 'text-red-300';
  return 'text-amber-300';
}

export function MaxHealthPage() {
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ['max-health', days],
    queryFn: async () => (await getMaxHealth(days)).data as MaxHealthResponse,
    refetchInterval: 5 * 60_000,
  });
  const d = q.data;
  const erro = q.error
    ? extractErrorMessage(q.error, 'erro ao carregar MAX Health')
    : d && !d.disponivel
      ? d.erro ?? 'dados indisponíveis'
      : null;

  if (q.isLoading) return <div className="p-6 text-sm text-slate-400">Carregando saúde do MAX…</div>;
  if (!d || erro)
    return (
      <Card className="border border-red-800/50 bg-red-950/20">
        <Title>MAX Health indisponível</Title>
        <Text className="mt-2 text-red-300">{erro ?? 'Sem resposta do serviço.'}</Text>
      </Card>
    );

  const max = d.reliability.max;
  const ctrl = d.reliability.control_standard;
  const estado = d.health.state;
  const Icone = estado === 'red' ? TriangleAlert : estado === 'green' ? CircleCheck : CircleDot;
  const corEstado =
    estado === 'red'
      ? 'border-red-800/60 bg-red-950/30 text-red-200'
      : estado === 'green'
        ? 'border-emerald-800/60 bg-emerald-950/30 text-emerald-200'
        : 'border-amber-800/60 bg-amber-950/30 text-amber-200';
  const rotuloEstado = estado === 'red' ? 'Ação necessária' : estado === 'green' ? 'Saudável' : 'Monitorando';
  const gates = Object.entries(d.quality.release_gates ?? {});

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Title>MAX Health</Title>
            <Text>
              Painel diário do canário MAX {d.rollout.target_pct}% · release {d.rollout.release_id} · 5 créditos
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-slate-700 p-0.5">
              {JANELAS.map((j) => (
                <button
                  key={j}
                  type="button"
                  onClick={() => setDays(j)}
                  className={`rounded px-2.5 py-1 text-xs ${days === j ? 'bg-blue-600/30 text-blue-200' : 'text-slate-400 hover:bg-slate-800'}`}
                >
                  {j}d
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => void q.refetch()}
              className="rounded-md border border-slate-700 p-1.5 text-slate-300 hover:bg-slate-800"
              title="Atualizar"
            >
              <RefreshCw className={`h-4 w-4 ${q.isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      <Card className={`border ${corEstado}`}>
        <div className="flex items-start gap-3">
          <Icone className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <div className="font-semibold">{rotuloEstado}</div>
            {[...d.health.blockers, ...d.health.warnings].map((m) => (
              <div key={m} className="mt-1 text-sm opacity-90">• {m}</div>
            ))}
          </div>
        </div>
      </Card>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-blue-300" />
          <Title>1. Correção e auditabilidade</Title>
        </div>
        <Grid numItems={1} numItemsSm={3} className="gap-4">
          <Kpi
            label="Entregas completas"
            value={`${max.completed - max.completed_missing_artifact}/${max.completed}`}
            hint="Concluídos com PDF + PJC; ausência de qualquer artefato é defeito crítico."
          />
          <Kpi
            label="Revisão humana cega"
            value={pct(d.quality.human_review.coverage_pct)}
            hint={`${d.quality.human_review.reviewed_live_cases}/${d.quality.human_review.completed_cases} casos live adjudicados.`}
          />
          <Kpi
            label="Divergência runner × banco"
            value={String(max.runner_db_mismatch)}
            hint="Runner falhou, mas banco aparece concluído (ou equivalente)."
          />
        </Grid>
        <Card className="border border-blue-900/50 bg-blue-950/20">
          <Text className="text-blue-200">{d.quality.human_review.note}</Text>
          {d.quality.human_review.pending_case_ids.length > 0 && (
            <div className="mt-2 text-xs text-slate-300">
              Fila de revisão: {d.quality.human_review.pending_case_ids.map((id) => `#${id}`).join(', ')}
            </div>
          )}
        </Card>
      </section>

      <section className="space-y-3">
        <Title>2. Rollout e adoção</Title>
        <Grid numItems={1} numItemsSm={4} className="gap-4">
          <Kpi
            label="Usuários elegíveis"
            value={String(d.rollout.eligible_new_users)}
            hint={`Criados após ${new Date(d.window.cutoff).toLocaleString('pt-BR')}.`}
          />
          <Kpi
            label="Atribuídos ao MAX"
            value={`${d.rollout.assigned_users} · ${pct(d.rollout.actual_assignment_pct)}`}
            hint={
              d.rollout.assignment_ci95
                ? `IC95 ${pct(d.rollout.assignment_ci95.low_pct)}–${pct(d.rollout.assignment_ci95.high_pct)}; alvo ${d.rollout.target_pct}%`
                : `alvo ${d.rollout.target_pct}%`
            }
          />
          <Kpi
            label="Escolheram MAX"
            value={`${d.rollout.users_who_selected_max}/${d.rollout.assigned_users}`}
            hint={`${pct(d.rollout.selection_rate_among_assigned_pct)} dos atribuídos; impressão real ainda não instrumentada.`}
          />
          <Kpi
            label="Casos MAX"
            value={String(max.total)}
            hint={`${max.completed} concluídos · ${max.failed_or_cancelled} falhos/cancelados · ${max.in_progress} em andamento.`}
          />
        </Grid>
        <Amostra n={d.rollout.eligible_new_users} status={d.rollout.sample_status} />
      </section>

      <section className="space-y-3">
        <Title>3. Confiabilidade e velocidade</Title>
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-3">Coorte</th>
                <th className="px-4 py-3">Casos</th>
                <th className="px-4 py-3">Conclusão</th>
                <th className="px-4 py-3">Falha/cancel.</th>
                <th className="px-4 py-3">Latência p50 / p95</th>
                <th className="px-4 py-3">Artefato ausente</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {([['MAX', max], ['Controle Padrão', ctrl]] as const).map(([rotulo, c]) => (
                <tr key={rotulo}>
                  <td className="px-4 py-3 font-medium text-slate-200">{rotulo}</td>
                  <td className="px-4 py-3">{c.total}</td>
                  <td className="px-4 py-3">
                    {pct(c.completion_rate.value_pct)}{' '}
                    <Amostra n={c.completion_rate.sample_n} status={c.completion_rate.status} />
                  </td>
                  <td className="px-4 py-3">{pct(c.failure_rate.value_pct)}</td>
                  <td className="px-4 py-3">
                    {minutos(c.latency_minutes_p50)} / {minutos(c.latency_minutes_p95)}
                  </td>
                  <td className="px-4 py-3">{c.completed_missing_artifact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <Title>4. Economia</Title>
        <Grid numItems={1} numItemsSm={4} className="gap-4">
          <Kpi
            label="Créditos líquidos"
            value={String(d.economics.net_credits)}
            hint={`${d.economics.credits_consumed} consumidos · ${d.economics.credits_refunded} estornados.`}
          />
          <Kpi
            label="Preço por caso"
            value={`${d.economics.credits_per_max_case_configured} créditos`}
            hint="Contrato configurado do produto MAX."
          />
          <Kpi
            label="Custo IA p50 / p95"
            value={`${usd(d.economics.cost_usd_p50)} / ${usd(d.economics.cost_usd_p95)}`}
            hint="Runner isolado; só é confiável com cobertura alta."
          />
          <Kpi
            label="Cobertura de custo"
            value={pct(d.economics.cost_coverage_pct)}
            hint="% de casos MAX com ai_cost_usd persistido."
          />
        </Grid>
      </section>

      <section className="space-y-3">
        <Title>5. Casos MAX recentes</Title>
        {d.cases.length === 0 ? (
          <Card>
            <Text>Nenhum caso da coorte live ainda.</Text>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-800">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-3">Caso</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">PDF/PJC</th>
                  <th className="px-4 py-3">Latência</th>
                  <th className="px-4 py-3">Custo</th>
                  <th className="px-4 py-3">Retries</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {d.cases.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-3 font-medium">#{c.id}</td>
                    <td className={`px-4 py-3 ${corStatus(c.status)}`}>{c.status}</td>
                    <td className="px-4 py-3">
                      {c.pdf ? '✓' : '—'} / {c.pjc ? '✓' : '—'}
                    </td>
                    <td className="px-4 py-3">{minutos(c.latency_minutes)}</td>
                    <td className="px-4 py-3">{usd(c.ai_cost_usd)}</td>
                    <td className="px-4 py-3">{c.retries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <Title>Release e lacunas de instrumentação</Title>
        <Grid numItems={1} numItemsLg={2} className="gap-4">
          <Card>
            <div className="font-medium text-slate-200">Gates do release</div>
            <div className="mt-3 space-y-2 text-sm">
              {gates.map(([nome, g]) => (
                <div key={nome} className="flex justify-between gap-3 border-b border-slate-800 pb-2 last:border-0">
                  <span className="text-slate-300">{nome.replace(/_/g, ' ')}</span>
                  <span className={(g.failed ?? 0) === 0 ? 'text-emerald-300' : 'text-amber-300'}>
                    {g.passed ?? 0} pass · {g.failed ?? 0} fail
                  </span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <div className="font-medium text-slate-200">O que ainda falta medir</div>
            <div className="mt-3 space-y-2 text-sm text-slate-300">
              {d.data_gaps.map((g) => (
                <div key={g}>• {g}</div>
              ))}
            </div>
          </Card>
        </Grid>
      </section>
    </div>
  );
}
